import React, { useState, useEffect } from 'react'
import {
  ArrowLeft,
  Plus,
  Save,
  RotateCcw,
  User,
  MapPin,
  Phone,
  Target,
  DollarSign,
  Calendar,
  Tag,
  HelpCircle
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import prospectMasterService from '../../services/prospectMasterService'
import { getStateOptions } from '../../constants/indianStates'

const ProspectMasterForm = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('prospectInfo')
  const [showHelp, setShowHelp] = useState(false)
  const [formData, setFormData] = useState({
    // Prospect Info - Matching backend model
    prospectCode: '',
    prospectName: '',
    prospectNickName: '',
    prospectCategory: '',
    prospectType: '',
    region: '', // Required field
    prospectUdyogAadhar: '',
    prospectPAN: '',
    GSTIN: '',
    GSTClassification: '',
    leadSource: '',
    leadScore: 0,
    prospectWebsite: '',
    expectedDealValue: '',
    expectedCloseDate: '',
    prospectStage: 'Lead',
    prospectStatus: 'Active',
    isProspectActive: 'A',
    notes: '',
    tags: []
  })

  const [addresses, setAddresses] = useState([
    {
      id: 1,
      country: '',
      stateProvince: '',
      cityDistrict: '',
      pinCode: '',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      addressLine4: ''
    }
  ])

  const [contacts, setContacts] = useState([
    {
      id: 1,
      contactPerson: '',
      email: '',
      mobile: '',
      alternatePhone: '',
      department: '',
      designation: ''
    }
  ])

  const [bankDetails, setBankDetails] = useState([
    {
      id: 1,
      bankName: '',
      accountNumber: '',
      ifscCode: '',
      branchName: ''
    }
  ])

  // Prospect code will be auto-generated by backend on save
  // No need to fetch preview code on form load

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleAddressChange = (index, field, value) => {
    const newAddresses = [...addresses]
    newAddresses[index] = { ...newAddresses[index], [field]: value }
    setAddresses(newAddresses)
  }

  const handleContactChange = (index, field, value) => {
    const newContacts = [...contacts]
    newContacts[index] = { ...newContacts[index], [field]: value }
    setContacts(newContacts)
  }

  const handleBankDetailChange = (index, field, value) => {
    const newBankDetails = [...bankDetails]
    newBankDetails[index] = { ...newBankDetails[index], [field]: value }
    setBankDetails(newBankDetails)
  }

  const addAddress = () => {
    setAddresses([...addresses, {
      id: addresses.length + 1,
      country: '',
      stateProvince: '',
      cityDistrict: '',
      pinCode: '',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      addressLine4: ''
    }])
  }

  const addContact = () => {
    setContacts([...contacts, {
      id: contacts.length + 1,
      contactPerson: '',
      email: '',
      mobile: '',
      alternatePhone: '',
      department: '',
      designation: ''
    }])
  }

  const addBankDetail = () => {
    setBankDetails([...bankDetails, {
      id: bankDetails.length + 1,
      bankName: '',
      accountNumber: '',
      ifscCode: '',
      branchName: ''
    }])
  }

  const removeAddress = (index) => {
    if (addresses.length > 1) {
      setAddresses(addresses.filter((_, i) => i !== index))
    }
  }

  const removeContact = (index) => {
    if (contacts.length > 1) {
      setContacts(contacts.filter((_, i) => i !== index))
    }
  }

  const removeBankDetail = (index) => {
    if (bankDetails.length > 1) {
      setBankDetails(bankDetails.filter((_, i) => i !== index))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      // Prospect code will be auto-generated by backend pre-save hook
      // Remove prospectCode from submission data to let backend generate it
      const { prospectCode, ...formDataWithoutCode } = formData;
      const submissionData = {
        ...formDataWithoutCode,
        prospectBillingAddress: addresses.map(addr => ({
          line1: addr.addressLine1,
          line2: addr.addressLine2,
          line3: addr.addressLine3,
          line4: addr.addressLine4,
          state: addr.stateProvince,
          city: addr.cityDistrict,
          pinCode: addr.pinCode,
          country: addr.country
        })),
        prospectContactInfo: contacts.map(contact => ({
          contactPersonName: contact.contactPerson,
          contactPersonDesignation: contact.designation,
          contactPersonDepartment: contact.department,
          contactPersonNumber: contact.mobile,
          contactPersonEmail: contact.email
        })),
        prospectBankDetails: bankDetails.map(bank => ({
          bankName: bank.bankName,
          accountNumber: bank.accountNumber,
          ifscCode: bank.ifscCode,
          branchName: bank.branchName
        }))
      }

      const response = await prospectMasterService.createProspect(submissionData)
      
      if (response.success) {
        toast.success('Prospect created successfully')
        navigate('/crm/prospect-master')
      } else {
        toast.error(response.message || 'Failed to create prospect')
      }
    } catch (error) {
      console.error('Error creating prospect:', error)
      toast.error('Failed to create prospect')
    }
  }

  const handleReset = () => {
    setFormData({
      prospectCode: '',
      prospectName: '',
      prospectNickName: '',
      prospectCategory: '',
      prospectType: '',
      region: '',
      prospectUdyogAadhar: '',
      prospectPAN: '',
      GSTIN: '',
      GSTClassification: '',
      leadSource: '',
      leadScore: 0,
      prospectWebsite: '',
      expectedDealValue: '',
      expectedCloseDate: '',
      prospectStage: 'Lead',
      prospectStatus: 'Active',
      isProspectActive: 'A',
      notes: '',
      tags: []
    })
    setAddresses([{
      id: 1,
      country: '',
      stateProvince: '',
      cityDistrict: '',
      pinCode: '',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      addressLine4: ''
    }])
    setContacts([{
      id: 1,
      contactPerson: '',
      email: '',
      mobile: '',
      alternatePhone: '',
      department: '',
      designation: ''
    }])
    setBankDetails([{
      id: 1,
      bankName: '',
      accountNumber: '',
      ifscCode: '',
      branchName: ''
    }])
  }

  const handleBack = () => {
    navigate('/crm/prospect-master')
  }

  const handleTestData = () => {
    // Set expected close date (30 days from today)
    const today = new Date();
    const expectedCloseDate = new Date(today);
    expectedCloseDate.setDate(today.getDate() + 30);

    // Populate test data for form fields
    setFormData(prev => ({
      ...prev,
      prospectName: 'Test Prospect Private Limited',
      prospectNickName: 'Test Prospect',
      prospectCategory: 'Enterprise',
      prospectType: 'Direct',
      region: 'South',
      prospectUdyogAadhar: 'UDYAM-TEST-123456',
      prospectPAN: 'ABCDE1234F',
      GSTIN: '36AALCD2019E1ZY',
      GSTClassification: 'Regular',
      leadSource: 'Website',
      leadScore: 75,
      prospectWebsite: 'https://www.testprospect.com',
      expectedDealValue: '500000',
      expectedCloseDate: expectedCloseDate.toISOString().split('T')[0],
      prospectStage: 'Qualified',
      prospectStatus: 'Active',
      isProspectActive: 'A',
      notes: 'This is a test prospect for demonstration purposes. High potential lead with strong interest in our services.',
      tags: ['Enterprise', 'High Value', 'Qualified']
    }));

    // Populate test address data
    setAddresses([{
      id: 1,
      country: 'India',
      stateProvince: 'Karnataka',
      cityDistrict: 'Bangalore',
      pinCode: '560001',
      addressLine1: '123 Test Street',
      addressLine2: 'Test Building, Floor 10',
      addressLine3: 'Test Area',
      addressLine4: 'Near Test Landmark'
    }]);

    // Populate test contact data
    setContacts([{
      id: 1,
      contactPerson: 'Jane Smith',
      email: 'jane.smith@testprospect.com',
      mobile: '9876543210',
      alternatePhone: '9876543211',
      department: 'Procurement',
      designation: 'Procurement Manager'
    }]);

    // Populate test bank details
    setBankDetails([{
      id: 1,
      bankName: 'Test Bank',
      accountNumber: '1234567890123456',
      ifscCode: 'TEST0001234',
      branchName: 'Bangalore Main Branch'
    }]);

    toast.success('Test data populated successfully!');
  };

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'CRM',
      path: '/crm',
      onClick: () => navigate('/crm')
    },
    {
      label: 'Masters',
      path: '/crm',
      onClick: () => navigate('/crm')
    },
    {
      label: 'Prospect Master',
      path: '/crm/prospect-master',
      onClick: () => navigate('/crm/prospect-master')
    },
    {
      label: 'New Prospect',
      path: '/crm/prospect-master/new',
      isCurrent: true
    }
  ]

  const helpContent = (
    <>
      <h3>What is Prospect Master?</h3>
      <p>Prospect Master lets you <strong>create and manage prospect information</strong>—potential customers before they become leads or customers. You can store category, region, lead source, stage, expected deal value, and close date, plus addresses, contacts, and bank details.</p>
      <h3>Tabs</h3>
      <p><strong>Prospect Info</strong> – Category, code (auto-generated on save), name, region, lead source, lead score, stage, status, expected deal value and close date, and optional fields like nick name and type.</p>
      <p><strong>Prospect Address</strong> – One or more addresses (country, state, city, pin, address lines).</p>
      <p><strong>Contact Details</strong> – Contact persons with email, mobile, department, and designation.</p>
      <p><strong>Bank Details</strong> – Bank name, account number, IFSC, and branch for payment-related information.</p>
      <h3>How to use</h3>
      <p>Fill required fields (marked with *), then use <strong>Save</strong> to create the prospect (prospect code is generated on save). Use <strong>Reset</strong> to clear the form or <strong>Cancel</strong> to go back to the list without saving.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Prospect Master Entry</h1>
          <p>Create and manage prospect information</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Prospect Master Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'prospectInfo' ? 'active' : ''}`}
            onClick={() => setActiveTab('prospectInfo')}
          >
            <User size={18} />
            <span>Prospect Info</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'addresses' ? 'active' : ''}`}
            onClick={() => setActiveTab('addresses')}
          >
            <MapPin size={18} />
            <span>Prospect Address</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'contacts' ? 'active' : ''}`}
            onClick={() => setActiveTab('contacts')}
          >
            <Phone size={18} />
            <span>Contact Details</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'bankDetails' ? 'active' : ''}`}
            onClick={() => setActiveTab('bankDetails')}
          >
            <DollarSign size={18} />
            <span>Bank Details</span>
          </button>
        </div>
          
        <form className="form-content">

          {/* Prospect Info Tab */}
          {activeTab === 'prospectInfo' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Prospect Information</h3>
              </div>
              
              {/* Row 1: Prospect Category, Prospect Code, Prospect Name, Region */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="prospectCategory" className="form-label required">
                    Prospect Category
                  </label>
                  <select
                    id="prospectCategory"
                    name="prospectCategory"
                    value={formData.prospectCategory}
                    onChange={(e) => handleInputChange('prospectCategory', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Category</option>
                    <option value="Enterprise">Enterprise</option>
                    <option value="SMB">SMB</option>
                    <option value="Startup">Startup</option>
                    <option value="Individual">Individual</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="prospectCode" className="form-label required">
                    Prospect Code
                  </label>
                  <input
                    type="text"
                    id="prospectCode"
                    name="prospectCode"
                    value="Auto-generated on save"
                    readOnly
                    className="form-input"
                    placeholder="Auto-generated on save"
                    style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed', fontStyle: 'italic' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="prospectName" className="form-label required">
                    Prospect Name
                  </label>
                  <input
                    type="text"
                    id="prospectName"
                    name="prospectName"
                    value={formData.prospectName}
                    onChange={(e) => handleInputChange('prospectName', e.target.value)}
                    className="form-input"
                    placeholder="Enter prospect name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="region" className="form-label required">
                    Region
                  </label>
                  <select
                    id="region"
                    name="region"
                    value={formData.region}
                    onChange={(e) => handleInputChange('region', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Region</option>
                    <option value="North">North</option>
                    <option value="South">South</option>
                    <option value="East">East</option>
                    <option value="West">West</option>
                    <option value="Central">Central</option>
                  </select>
                </div>
              </div>

              {/* Row 2: Lead Source, Lead Score, Prospect Stage, Status */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="leadSource" className="form-label">
                    Lead Source
                  </label>
                  <select
                    id="leadSource"
                    name="leadSource"
                    value={formData.leadSource}
                    onChange={(e) => handleInputChange('leadSource', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Lead Source</option>
                    <option value="Website">Website</option>
                    <option value="Referral">Referral</option>
                    <option value="Cold Call">Cold Call</option>
                    <option value="Email Campaign">Email Campaign</option>
                    <option value="Social Media">Social Media</option>
                    <option value="Trade Show">Trade Show</option>
                    <option value="Partner">Partner</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="leadScore" className="form-label">
                    Lead Score (0-100)
                  </label>
                  <input
                    type="number"
                    id="leadScore"
                    name="leadScore"
                    min="0"
                    max="100"
                    value={formData.leadScore}
                    onChange={(e) => handleInputChange('leadScore', parseInt(e.target.value) || 0)}
                    className="form-input"
                    placeholder="0"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="prospectStage" className="form-label">
                    Prospect Stage
                  </label>
                  <select
                    id="prospectStage"
                    name="prospectStage"
                    value={formData.prospectStage}
                    onChange={(e) => handleInputChange('prospectStage', e.target.value)}
                    className="form-input"
                  >
                    <option value="Lead">Lead</option>
                    <option value="Qualified">Qualified</option>
                    <option value="Proposal">Proposal</option>
                    <option value="Negotiation">Negotiation</option>
                    <option value="Closed Won">Closed Won</option>
                    <option value="Closed Lost">Closed Lost</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="prospectStatus" className="form-label">
                    Status
                  </label>
                  <select
                    id="prospectStatus"
                    name="prospectStatus"
                    value={formData.prospectStatus}
                    onChange={(e) => handleInputChange('prospectStatus', e.target.value)}
                    className="form-input"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Converted">Converted</option>
                    <option value="Lost">Lost</option>
                  </select>
                </div>
              </div>

              {/* Row 3: Expected Deal Value, Expected Close Date, Nick Name, Type */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="expectedDealValue" className="form-label">
                    Expected Deal Value
                  </label>
                  <input
                    type="number"
                    id="expectedDealValue"
                    name="expectedDealValue"
                    value={formData.expectedDealValue}
                    onChange={(e) => handleInputChange('expectedDealValue', e.target.value)}
                    className="form-input"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="expectedCloseDate" className="form-label">
                    Expected Close Date
                  </label>
                  <input
                    type="date"
                    id="expectedCloseDate"
                    name="expectedCloseDate"
                    value={formData.expectedCloseDate}
                    onChange={(e) => handleInputChange('expectedCloseDate', e.target.value)}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="prospectNickName" className="form-label">
                    Nick Name
                  </label>
                  <input
                    type="text"
                    id="prospectNickName"
                    name="prospectNickName"
                    value={formData.prospectNickName}
                    onChange={(e) => handleInputChange('prospectNickName', e.target.value)}
                    className="form-input"
                    placeholder="Enter nick name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="prospectType" className="form-label">
                    Type
                  </label>
                  <select
                    id="prospectType"
                    name="prospectType"
                    value={formData.prospectType}
                    onChange={(e) => handleInputChange('prospectType', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Type</option>
                    <option value="Direct">Direct</option>
                    <option value="Partner">Partner</option>
                    <option value="Referral">Referral</option>
                    <option value="Website">Website</option>
                  </select>
                </div>
              </div>

              {/* Row 4: Website, PAN Number, GSTIN, GST Classification */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="prospectWebsite" className="form-label">
                    Website
                  </label>
                  <input
                    type="url"
                    id="prospectWebsite"
                    name="prospectWebsite"
                    value={formData.prospectWebsite}
                    onChange={(e) => handleInputChange('prospectWebsite', e.target.value)}
                    className="form-input"
                    placeholder="https://example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="prospectPAN" className="form-label">
                    PAN Number
                  </label>
                  <input
                    type="text"
                    id="prospectPAN"
                    name="prospectPAN"
                    value={formData.prospectPAN}
                    onChange={(e) => handleInputChange('prospectPAN', e.target.value)}
                    className="form-input"
                    placeholder="Enter PAN number"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="GSTIN" className="form-label">
                    GSTIN
                  </label>
                  <input
                    type="text"
                    id="GSTIN"
                    name="GSTIN"
                    value={formData.GSTIN}
                    onChange={(e) => handleInputChange('GSTIN', e.target.value)}
                    className="form-input"
                    placeholder="Enter GSTIN"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="GSTClassification" className="form-label">
                    GST Classification
                  </label>
                  <select
                    id="GSTClassification"
                    name="GSTClassification"
                    value={formData.GSTClassification}
                    onChange={(e) => handleInputChange('GSTClassification', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Classification</option>
                    <option value="Regular">Regular</option>
                    <option value="Composition">Composition</option>
                    <option value="Unregistered">Unregistered</option>
                  </select>
                </div>
              </div>

              {/* Row 5: Notes (full width) */}
              <div className="grid-row-4">
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="notes" className="form-label">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    className="form-input"
                    rows="3"
                    placeholder="Enter additional notes"
                  />
                </div>
              </div>
          </div>
        )}

          {/* Prospect Address Tab */}
          {activeTab === 'addresses' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Prospect Addresses</h3>
              </div>
              
              {addresses.map((address, index) => (
                <div key={address.id} className="address-section">
                  <div className="address-header">
                    <h3>
                      {index === 0 ? 'Primary Address' : `Address ${index + 1}`}
                    </h3>
                    {index === 0 && (
                      <button 
                        type="button" 
                        className="add-address-button"
                        onClick={addAddress}
                      >
                        <Plus size={16} />
                      </button>
                    )}
                    {index > 0 && (
                      <button 
                        type="button" 
                        className="remove-address-button"
                        onClick={() => removeAddress(index)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  {/* Row 1: Country, State/Province, City/District, Pin Code */}
                  <div className="grid-row-4">
                    <div className="form-group">
                      <label className="form-label required">Country</label>
                      <select
                        value={address.country}
                        onChange={(e) => handleAddressChange(index, 'country', e.target.value)}
                        className="form-input"
                      >
                        <option value="">Select Country</option>
                        <option value="India">India</option>
                        <option value="USA">USA</option>
                        <option value="UK">UK</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label required">State/Province</label>
                      <select
                        value={address.stateProvince}
                        onChange={(e) => handleAddressChange(index, 'stateProvince', e.target.value)}
                        className="form-input"
                      >
                        <option value="">Select State</option>
                        {getStateOptions().map(state => (
                          <option key={state.value} value={state.value}>
                            {state.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label required">City/District</label>
                      <input
                        type="text"
                        value={address.cityDistrict}
                        onChange={(e) => handleAddressChange(index, 'cityDistrict', e.target.value)}
                        className="form-input"
                        placeholder="Enter city"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Pin Code</label>
                      <input
                        type="text"
                        value={address.pinCode}
                        onChange={(e) => handleAddressChange(index, 'pinCode', e.target.value)}
                        className="form-input"
                        placeholder="Enter pin code"
                      />
                    </div>
                  </div>

                  {/* Row 2: Address Lines */}
                  <div className="grid-row-4">
                    <div className="form-group">
                      <label className="form-label required">Address Line 1</label>
                      <input
                        type="text"
                        value={address.addressLine1}
                        onChange={(e) => handleAddressChange(index, 'addressLine1', e.target.value)}
                        className="form-input"
                        placeholder="Enter address line 1"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Address Line 2</label>
                      <input
                        type="text"
                        value={address.addressLine2}
                        onChange={(e) => handleAddressChange(index, 'addressLine2', e.target.value)}
                        className="form-input"
                        placeholder="Enter address line 2"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Address Line 3</label>
                      <input
                        type="text"
                        value={address.addressLine3}
                        onChange={(e) => handleAddressChange(index, 'addressLine3', e.target.value)}
                        className="form-input"
                        placeholder="Enter address line 3"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Address Line 4</label>
                      <input
                        type="text"
                        value={address.addressLine4}
                        onChange={(e) => handleAddressChange(index, 'addressLine4', e.target.value)}
                        className="form-input"
                        placeholder="Enter address line 4"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Contact Details Tab */}
          {activeTab === 'contacts' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Contact Information</h3>
              </div>
              
              {contacts.map((contact, index) => (
                <div key={contact.id} className="contact-section">
                  <div className="contact-header">
                    <h3>
                      {index === 0 ? 'Primary Contact' : `Contact ${index + 1}`}
                    </h3>
                    {index === 0 && (
                      <button 
                        type="button" 
                        className="add-contact-button"
                        onClick={addContact}
                      >
                        <Plus size={16} />
                      </button>
                    )}
                    {index > 0 && (
                      <button 
                        type="button" 
                        className="remove-contact-button"
                        onClick={() => removeContact(index)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  {/* Row 1: Contact Person, Mobile, Alternate Phone, Email */}
                  <div className="grid-row-4">
                    <div className="form-group">
                      <label className="form-label required">Contact Person</label>
                      <input
                        type="text"
                        value={contact.contactPerson}
                        onChange={(e) => handleContactChange(index, 'contactPerson', e.target.value)}
                        className="form-input"
                        placeholder="Enter contact person name"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Mobile</label>
                      <input
                        type="tel"
                        value={contact.mobile}
                        onChange={(e) => handleContactChange(index, 'mobile', e.target.value)}
                        className="form-input"
                        placeholder="Enter mobile number"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Alternate Phone</label>
                      <input
                        type="tel"
                        value={contact.alternatePhone}
                        onChange={(e) => handleContactChange(index, 'alternatePhone', e.target.value)}
                        className="form-input"
                        placeholder="Enter alternate phone"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Email</label>
                      <input
                        type="email"
                        value={contact.email}
                        onChange={(e) => handleContactChange(index, 'email', e.target.value)}
                        className="form-input"
                        placeholder="Enter email address"
                      />
                    </div>
                  </div>

                  {/* Row 2: Department, Designation */}
                  <div className="grid-row-4">
                    <div className="form-group">
                      <label className="form-label">Department</label>
                      <input
                        type="text"
                        value={contact.department}
                        onChange={(e) => handleContactChange(index, 'department', e.target.value)}
                        className="form-input"
                        placeholder="Enter department"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Designation</label>
                      <input
                        type="text"
                        value={contact.designation}
                        onChange={(e) => handleContactChange(index, 'designation', e.target.value)}
                        className="form-input"
                        placeholder="Enter designation"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Bank Details Tab */}
          {activeTab === 'bankDetails' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Bank Details</h3>
              </div>
              
              {bankDetails.map((bank, index) => (
                <div key={bank.id} className="bank-section">
                  <div className="bank-header">
                    <h3>
                      {index === 0 ? 'Primary Bank Detail' : `Bank Detail ${index + 1}`}
                    </h3>
                    {index === 0 && (
                      <button 
                        type="button" 
                        className="add-bank-button"
                        onClick={addBankDetail}
                      >
                        <Plus size={16} />
                      </button>
                    )}
                    {index > 0 && (
                      <button 
                        type="button" 
                        className="remove-bank-button"
                        onClick={() => removeBankDetail(index)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  {/* Row 1: Bank Name, Account Number, IFSC Code, Branch Name */}
                  <div className="grid-row-4">
                    <div className="form-group">
                      <label className="form-label">Bank Name</label>
                      <input
                        type="text"
                        value={bank.bankName}
                        onChange={(e) => handleBankDetailChange(index, 'bankName', e.target.value)}
                        className="form-input"
                        placeholder="Enter bank name"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Account Number</label>
                      <input
                        type="text"
                        value={bank.accountNumber}
                        onChange={(e) => handleBankDetailChange(index, 'accountNumber', e.target.value)}
                        className="form-input"
                        placeholder="Enter account number"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">IFSC Code</label>
                      <input
                        type="text"
                        value={bank.ifscCode}
                        onChange={(e) => handleBankDetailChange(index, 'ifscCode', e.target.value)}
                        className="form-input"
                        placeholder="Enter IFSC code"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Branch Name</label>
                      <input
                        type="text"
                        value={bank.branchName}
                        onChange={(e) => handleBankDetailChange(index, 'branchName', e.target.value)}
                        className="form-input"
                        placeholder="Enter branch name"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Action Buttons - Available in all tabs */}
          <div className="form-actions" style={{ position: 'relative' }}>
            {/* Test Button - Bottom Left */}
            <button
              type="button"
              onClick={handleTestData}
              className="btn-secondary"
              style={{
                position: 'absolute',
                bottom: '0',
                left: '0',
                zIndex: 10
              }}
            >
              Test
            </button>
            <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
              <button
                type="button"
                onClick={handleReset}
                className="btn-secondary"
              >
                <RotateCcw size={16} />
                Reset
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="btn-primary"
              >
                <Save size={16} />
                Save
              </button>
            </div>
          </div>
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Prospect Master Entry – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default ProspectMasterForm
